import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { BIRTH_ORDER_MAX, BIRTH_WEIGHT_GRAMS, eatDayOf, formatIsoDay } from '@wodi/domain';
import {
  Button,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import {
  birthEnteredInError,
  birthFormOptionsQuery,
  birthQuery,
  recordBirth,
} from '../api/vital-events.ts';
import { visitQuery } from '../api/visits.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { BirthSummaryPrinter } from './prints.tsx';
import styles from './vital-events.module.css';
import {
  DEATH_STATUS_TAGS,
  eatClockNow,
  eatInstant,
  errorText,
  gramsText,
  validTime,
} from './words.ts';

// Birth notification (docs/plans/phase2-mortuary-and-notifications.md §6): a live birth in the
// facility, recorded from the mother's open visit, which registers the baby linked to her.
// For review by the clinical safety lead.

const recordRoute = getRouteApi('/_app/visits/$visitId/record-birth');
const birthRoute = getRouteApi('/_app/births/$birthId');

const BIRTH_VISIT_TYPES: readonly string[] = ['outpatient', 'walk_in', 'emergency', 'inpatient'];

export function RecordBirthPage() {
  const { visitId } = recordRoute.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const visit = useQuery(visitQuery(visitId));
  const options = useQuery(birthFormOptionsQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [day, setDay] = useState<string | null>(formatIsoDay(eatDayOf(new Date())));
  const [time, setTime] = useState(() => eatClockNow());
  const [sex, setSex] = useState<string>('');
  const [weight, setWeight] = useState('');
  const [attendant, setAttendant] = useState<string | null>(session?.user.id ?? null);
  const [order, setOrder] = useState('');
  const mayRecord = session !== null && can(session, 'births.record');
  const grams = Number(weight);
  const weightOk =
    Number.isInteger(grams) && grams >= BIRTH_WEIGHT_GRAMS.min && grams <= BIRTH_WEIGHT_GRAMS.max;
  const orderValue = Number(order);
  const orderOk =
    order === '' ||
    (Number.isInteger(orderValue) && orderValue >= 1 && orderValue <= BIRTH_ORDER_MAX);
  const sexes = options.data?.sexes ?? [];
  const chosenSex = sexes.find((s) => `${s.system}|${s.code}` === sex);
  const record = useMutation({
    mutationFn: () =>
      recordBirth(visitId, {
        clientRequestId,
        bornAt: eatInstant(day ?? '', time),
        sex: { system: chosenSex?.system ?? '', code: chosenSex?.code ?? '' },
        birthWeightGrams: grams,
        attendantUserId: attendant ?? '',
        birthOrder: order === '' ? null : orderValue,
      }),
    onSuccess: async (birth) => {
      await queryClient.invalidateQueries({ queryKey: ['births'] });
      toasts.show({ title: 'Birth recorded', description: birth.baby.displayName });
      await navigate({ to: '/births/$birthId', params: { birthId: birth.id } });
    },
  });

  if (visit.isPending) return <LoadingBlock label="Loading the visit" />;
  if (visit.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void visit.refetch()}>Try again</Button>}>
        {errorText(visit.error, "The visit couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const v = visit.data;
  const eligible = v.status === 'open' && BIRTH_VISIT_TYPES.includes(v.type);
  const ready =
    online &&
    mayRecord &&
    eligible &&
    day !== null &&
    validTime(time) &&
    chosenSex !== undefined &&
    weightOk &&
    orderOk &&
    attendant !== null &&
    !record.isPending;
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={v.patientId} />
      <PageHeader
        title="Record birth"
        description="A live birth to this mother. The baby is registered as a new patient, linked to her."
      />
      {!mayRecord ? (
        <Notice tone="info">
          You don&apos;t have permission to record births. Ask the midwife or clinician who
          attended.
        </Notice>
      ) : !eligible ? (
        <Notice tone="info">
          A birth is recorded on the mother&apos;s open visit or stay. Open her current visit.
        </Notice>
      ) : (
        <Section title="Birth">
          <form
            className={styles.form}
            aria-label="Record birth"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) record.mutate();
            }}
          >
            <div className={styles.row}>
              <DateField label="Date of birth" value={day} onChange={setDay} />
              <TextField
                label="Time of birth"
                description="24-hour, like 14:05"
                value={time}
                onChange={setTime}
                width="sm"
                {...(time !== '' && !validTime(time)
                  ? { errorMessage: 'Write the time as hours and minutes, like 14:05.' }
                  : {})}
              />
            </div>
            <RadioGroup
              label="Sex"
              orientation="horizontal"
              value={sex}
              onChange={setSex}
              options={sexes.map((s) => ({ id: `${s.system}|${s.code}`, label: s.display }))}
            />
            <div className={styles.row}>
              <TextField
                label="Birth weight"
                unit="g"
                inputMode="numeric"
                value={weight}
                onChange={setWeight}
                width="sm"
                {...(weight !== '' && !weightOk
                  ? { errorMessage: 'Write the weight in whole grams, like 3150.' }
                  : {})}
              />
              <TextField
                label="Birth order (optional)"
                description="For twins and more: 1 for the first born, 2 for the second."
                inputMode="numeric"
                value={order}
                onChange={setOrder}
                width="sm"
                {...(orderOk
                  ? {}
                  : { errorMessage: `A number from 1 to ${String(BIRTH_ORDER_MAX)}.` })}
              />
            </div>
            <Select
              label="Attended by"
              placeholder="Choose one"
              options={(options.data?.attendants ?? []).map((a) => ({
                id: a.id,
                label: a.displayName,
              }))}
              value={attendant}
              onChange={(id) => {
                setAttendant(typeof id === 'string' ? id : null);
              }}
            />
            {!online ? (
              <Notice tone="info">
                No connection to Wodi. Recording a birth waits for the connection; nothing is saved
                on this device.
              </Notice>
            ) : null}
            {record.error === null ? null : (
              <Notice tone="error">
                {errorText(record.error, "The birth wasn't recorded. Try again.")}
              </Notice>
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!ready}
                isPending={record.isPending}
                pendingLabel="Recording…"
              >
                Record birth
              </Button>
            </div>
          </form>
        </Section>
      )}
    </div>
  );
}

export function BirthPage() {
  const { birthId } = birthRoute.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const birth = useQuery(birthQuery(birthId));
  const online = useOnline();
  const queryClient = useQueryClient();
  const [printing, setPrinting] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const correct = useMutation({
    mutationFn: (reason: string) => birthEnteredInError(birthId, reason),
    onSuccess: (view) => {
      queryClient.setQueryData(birthQuery(birthId).queryKey, view);
      setCorrecting(false);
      toasts.show({ title: 'Birth notification marked entered in error' });
    },
  });
  if (birth.isPending) return <LoadingBlock label="Loading the birth notification" rows={5} />;
  if (birth.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void birth.refetch()}>Try again</Button>}>
        {errorText(birth.error, "The birth notification couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const b = birth.data;
  const [word, shape] = DEATH_STATUS_TAGS[b.status];
  const mayCorrect = session !== null && can(session, 'births.correct');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={b.baby.id} />
      <PageHeader
        title="Birth notification"
        description={`Recorded ${formatInstant(b.recordedAt)} by ${b.recordedBy.displayName}.`}
        actions={
          b.status === 'recorded' ? (
            <>
              <Button
                onPress={() => {
                  setPrinting((p) => !p);
                }}
              >
                {printing ? 'Hide summary' : 'Print notification summary'}
              </Button>
              {mayCorrect ? (
                <Button
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    setCorrecting(true);
                  }}
                >
                  Mark entered in error
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {b.enteredInError === null ? null : (
        <Notice tone="info">
          {`Entered in error on ${formatInstant(b.enteredInError.at)} by ${b.enteredInError.by.displayName}: ${b.enteredInError.reason}. The baby's record stays.`}
        </Notice>
      )}
      {printing ? <BirthSummaryPrinter birthId={b.id} /> : null}
      <Section title="Birth" actions={<Tag state={shape}>{word}</Tag>}>
        <dl className={styles.facts}>
          <dt>Date and time</dt>
          <dd>{formatInstant(b.bornAt)}</dd>
          <dt>Outcome</dt>
          <dd>Live birth</dd>
          <dt>Sex</dt>
          <dd>{b.sex.display}</dd>
          <dt>Birth weight</dt>
          <dd>{gramsText(b.birthWeightGrams)}</dd>
          {b.birthOrder === null ? null : (
            <>
              <dt>Birth order</dt>
              <dd>{String(b.birthOrder)}</dd>
            </>
          )}
          <dt>Attended by</dt>
          <dd>{b.attendant.displayName}</dd>
          <dt>Mother</dt>
          <dd>
            <Link to="/patients/$patientId" params={{ patientId: b.mother.id }}>
              {b.mother.displayName}
            </Link>{' '}
            <span className="wodi-id">{b.mother.patientNumber}</span>
          </dd>
          <dt>Baby</dt>
          <dd>
            <Link to="/patients/$patientId" params={{ patientId: b.baby.id }}>
              {b.baby.displayName}
            </Link>{' '}
            <span className="wodi-id">{b.baby.patientNumber}</span>
          </dd>
        </dl>
      </Section>
      <ReasonDialog
        title="Mark entered in error"
        isOpen={correcting}
        onOpenChange={setCorrecting}
        confirmLabel="Mark entered in error"
        pendingLabel="Marking…"
        isPending={correct.isPending}
        error={correct.error === null ? null : errorText(correct.error, 'That did not work.')}
        onConfirm={(reason) => {
          correct.mutate(reason);
        }}
      />
    </div>
  );
}
